import { useLocation, useNavigate } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, DatePicker, Input, Segmented, Space, Statistic, Table, Tag, Typography } from 'antd';
import dayjs from 'dayjs';

import { SearchOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import { airportLabel, formatCategory } from '../data/airportLabel';
import { listCharterBookings, updateCharterBooking } from '../api/endpoints';
import { ADHOC_COLORS, AdhocTag } from '../components/AdhocTag';
import type { CharterBookingRow, CharterBookingStatus } from '../api/types';
import { gbp, amount } from '../data/format';
import { fmtZoned } from '../data/airportTime';
import { CHARTER_BOOKING_STATUS_COLORS, CHARTER_BOOKING_STATUS_LABELS, statusLabel } from '../data/statuses';
import { useUrlParams } from '../hooks/useUrlParams';

const { Title, Text } = Typography;

// SHAR-792 — Completed is a PAST-tab counter and nothing else.
//
// A completed booking is one whose arrival has already happened, so on Upcoming
// the number is zero by construction and a permanently-empty chip would just be
// a thing to explain. `pastOnly` keeps it off that tab rather than showing a
// count that can never move.
//
// SHAR-1255 — no "Created" card: charter bookings are no longer made in that
// status (every customer booking starts Pre-approved, SHAR-778/1129, and the
// admin's create-from-quote path is gone), so the card could only ever show
// older rows; they stay under "Total bookings".
//
// SHAR-1027 — "Passport needed" is the last card, as on the Bookings list
// (SHAR-451): it counts the ad-hoc label, and clicking it filters to the
// bookings carrying it instead of to a status.
const COUNTERS: {
  label: string;
  status?: CharterBookingStatus;
  pastOnly?: boolean;
  passportNeeded?: boolean;
  /** SHAR-131 — the "Wire transfer required" card, a filter like Passport needed. */
  wireTransfer?: boolean;
}[] = [
  { label: 'Total bookings' },
  { label: 'Pre-approved', status: 'pre_approved' },
  { label: 'Confirmed', status: 'confirmed' },
  { label: 'Completed', status: 'complete', pastOnly: true },
  { label: 'Cancelled', status: 'cancelled' },
  { label: 'Passport needed', passportNeeded: true },
  { label: 'Wire transfer required', wireTransfer: true },
];

export function CharterBookingsListPage() {
  const nav = useNavigate();
  const loc = useLocation();
  // SHAR-1256 — every filter lives in the page address, as on Bookings and
  // Charters (useUrlParams): "Back" from a booking, a reload or a pasted link
  // brings back the card, tab, search, dates and page.
  const [sp, setParams] = useUrlParams();
  const flag = (b: boolean) => (b ? '1' : null);
  const status = (sp.get('status') || undefined) as CharterBookingStatus | undefined;
  // SHAR-1027 — the "Passport needed" card's filter. One card is active at a
  // time, so selecting it clears the status and selecting a status clears it.
  const passportNeeded = sp.get('passport') === '1';
  // SHAR-131 — the "Wire transfer required" card's filter, same rules.
  const wireTransfer = sp.get('wire') === '1';
  // Upcoming first, as on the charters and bookings lists: it is the side an
  // admin works on, and the side the counters describe.
  const when = (sp.get('tab') === 'past' ? 'past' : 'upcoming') as 'upcoming' | 'past';
  const page = Math.max(1, Number(sp.get('page') ?? 1) || 1);
  const pageSize = 20;
  // SHAR-873 — the search box the Bookings page has, matched to it.
  const q = sp.get('q') ?? '';
  // SHAR-119 — departure date range, inclusive, as the two strings the API takes.
  const range = { from: sp.get('from') || undefined, to: sp.get('to') || undefined };
  const setStatus = (v: CharterBookingStatus | undefined) => setParams({ status: v ?? null });
  const setPassportNeeded = (b: boolean) => setParams({ passport: flag(b) });
  const setWireTransfer = (b: boolean) => setParams({ wire: flag(b) });
  const setWhen = (v: 'upcoming' | 'past') => setParams({ tab: v });
  const setPage = (v: number) => setParams({ page: v > 1 ? String(v) : null });
  const setQ = (v: string) => setParams({ q: v || null });
  const setRange = (r: { from?: string; to?: string }) => setParams({ from: r.from ?? null, to: r.to ?? null });

  // SHAR-1028 / SHAR-1027 - removing an ad-hoc label ("Wire transfer required",
  // "Passport needed") from a row.
  const qc = useQueryClient();
  const dismissAdhoc = useMutation({
    mutationFn: ({ bookingId, label }: { bookingId: string; label: 'transfer' | 'passport' }) =>
      updateCharterBooking(
        bookingId,
        label === 'transfer' ? { dismissAwaitingTransfer: true } : { dismissPassportNeeded: true },
      ),
    onSuccess: (_data, v) => {
      qc.invalidateQueries({ queryKey: ['charter-bookings'] });
      // SHAR-1105 — and the booking's own page (the admin caches queries for 30 s).
      qc.invalidateQueries({ queryKey: ['charter-booking', v.bookingId] });
    },
  });

  const list = useQuery({
    queryKey: ['charter-bookings', status, passportNeeded, wireTransfer, q, page, when, range.from, range.to],
    queryFn: () =>
      listCharterBookings({
        status,
        q: q || undefined,
        page,
        pageSize,
        when,
        from: range.from,
        to: range.to,
        passportNeeded: passportNeeded || undefined,
        wireTransfer: wireTransfer || undefined,
      }),
    // SHAR-845 — the counters and the rows come from this one response, so a
    // chip click changed the query key and React Query fell back to "no data
    // yet": the table blinked to its skeleton and every counter to zero for a
    // frame. Keeping the previous page on screen while the new one loads
    // removes the flash, and the counters are unaffected by the chip anyway
    // (the server groups them before the status filter), so what stays on
    // screen for that frame is already the right number.
    //
    // Bookings solves this with a SEPARATE counts query, which the ticket names
    // as the reference. There is no counts endpoint for charters, so copying
    // that shape here would mean a second round trip for numbers the list
    // already carries. Same outcome, one request.
    placeholderData: keepPreviousData,
  });

  const counters = list.data?.counters;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Title level={3} style={{ margin: 0 }}>Charter bookings</Title>
        <Text type="secondary">Bookings made from charter quotes.</Text>
      </div>

      {/* Same counter strip as Charters and Bookings: a fixed flex basis so the
          cards keep their width whatever the label reads, and the active one
          marked by both a border and a ring. The AC asks for these counters to
          double as the list's filter, which is what the click does. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, width: '100%' }}>
        {COUNTERS.filter((c) => !c.pastOnly || when === 'past').map((c) => {
          const active = c.passportNeeded
            ? passportNeeded
            : c.wireTransfer
              ? wireTransfer
              : c.status
                ? status === c.status
                : !status && !passportNeeded && !wireTransfer;
          const value = c.passportNeeded
            ? (counters?.passportNeeded ?? 0)
            : c.wireTransfer
              ? (counters?.wireTransfer ?? 0)
              : c.status
              ? (counters?.[c.status] ?? 0)
              : (counters?.total ?? 0);
          return (
            <Card
              key={c.label}
              size="small"
              hoverable
              loading={list.isLoading}
              onClick={() => {
                setStatus(c.status);
                setPassportNeeded(!!c.passportNeeded);
                setWireTransfer(!!c.wireTransfer);
                setPage(1);
              }}
              style={{
                // Regression sweep, 31 Aug: the status pill inside the card is
                // one line and does not shrink, so a long label ("Gathering
                // passengers") ran past the card's right edge at 130. The basis
                // fits the longest label the statuses can produce, and the pill
                // is allowed to wrap if a longer one is ever added.
                flex: '1 1 168px',
                minWidth: 168,
                cursor: 'pointer',
                borderColor: active ? '#1677ff' : undefined,
                boxShadow: active ? '0 0 0 1px #1677ff' : undefined,
              }}
              styles={{ body: { padding: '12px 16px' } }}
            >
              <Statistic
                title={
                  c.passportNeeded ? (
                    <Tag color={ADHOC_COLORS.passportNeededList} style={{ marginInlineEnd: 0, whiteSpace: 'normal' }}>
                      {c.label}
                    </Tag>
                  ) : c.wireTransfer ? (
                    <Tag color={ADHOC_COLORS.awaitingTransfer} style={{ marginInlineEnd: 0, whiteSpace: 'normal' }}>
                      {c.label}
                    </Tag>
                  ) : c.status ? (
                    <Tag color={CHARTER_BOOKING_STATUS_COLORS[c.status]} style={{ marginInlineEnd: 0, whiteSpace: 'normal' }}>
                      {c.label}
                    </Tag>
                  ) : (
                    c.label
                  )
                }
                value={value}
              />
            </Card>
          );
        })}
      </div>

      <Card>
        {/* SHAR-873 — the same toolbar as Bookings: search on the left,
            Upcoming/Past on the far right. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            // SHAR-1039 — search and date range sit together on the left; only
            // the Upcoming/Past switch is pushed to the right (marginLeft auto).
            justifyContent: 'flex-start',
            gap: 16,
            flexWrap: 'wrap',
            marginBottom: 12,
          }}
        >
          <Input
            // re-created when the address's search changes (browser back/forward),
            // so the box always shows the search the list is filtered by
            key={q}
            prefix={<SearchOutlined />}
            placeholder="Search by booking ID, user, route, flight ID, operator"
            defaultValue={q}
            onPressEnter={(e) => {
              setQ((e.target as HTMLInputElement).value);
              setPage(1);
            }}
            onChange={(e) => {
              if (!e.target.value) {
                setQ('');
                setPage(1);
              }
            }}
            style={{ flex: '1 1 320px', maxWidth: 480 }}
            allowClear
          />
          {/* SHAR-119 — the departure date range, beside the search it narrows.
              Both ends are inclusive and either may be left empty. */}
          <DatePicker.RangePicker
            allowEmpty={[true, true]}
            // shows the range the list is filtered by, also after Back or a reload
            value={[range.from ? dayjs(range.from) : null, range.to ? dayjs(range.to) : null]}
            format="DD/MM/YYYY"
            placeholder={['Departure from', 'Departure to']}
            onChange={(vals) => {
              const iso = (d: Dayjs | null | undefined) => (d ? d.format('YYYY-MM-DD') : undefined);
              setRange({ from: iso(vals?.[0]), to: iso(vals?.[1]) });
              setPage(1);
            }}
            // Admin regression 6 Oct — never narrower than its placeholders
            // ("Departure from" / "Departure to"), as on Bookings.
            style={{ flex: '0 1 300px', minWidth: 280 }}
          />
          <Segmented
            value={when}
            onChange={(v) => {
              const next = v as 'upcoming' | 'past';
              setWhen(next);
              // SHAR-792 — leaving Past takes its Completed chip off screen, so
              // the filter it set has to go with it. Otherwise Upcoming renders
              // an empty table filtered by a chip that is no longer there.
              if (next !== 'past' && status === 'complete') setStatus(undefined);
              setPage(1);
            }}
            options={[
              { label: 'Upcoming', value: 'upcoming' },
              { label: 'Past', value: 'past' },
            ]}
            style={{ marginLeft: 'auto' }}
          />
        </div>
        {/* The AC lists sixteen fields per booking, so the table scrolls
            horizontally rather than squashing every column, as the requests and
            bookings lists already do. */}
        <Table<CharterBookingRow>
          className="no-header-break"
          rowKey="id"
          loading={list.isLoading}
          dataSource={list.data?.data || []}
          onRow={(r) => ({
            // the list's own address, so "Back" returns to it as it was (SHAR-1256)
            onClick: () => nav(`/charter-bookings/${r.id}`, { state: { from: `${loc.pathname}${loc.search}` } }),
            style: { cursor: 'pointer' },
          })}
          // SHAR-999 (Copilot review) — the scroll width is the sum of the
          // declared columns (2,340 after the Request column was widened;
          // 2,510 with the SHAR-1028 Ad-hoc status column; 2,540 since SHAR-131
          // widened that column from 170 to 200).
          // Below it antd squeezes the columns back and the header this ticket
          // is about breaks again.
          scroll={{ x: 2540 }}
          pagination={{
            current: page,
            total: list.data?.total || 0,
            pageSize,
            onChange: setPage,
            showSizeChanger: false,
            showTotal: (t) => `${t} bookings`,
          }}
          columns={[
            {
              title: 'Booking',
              key: 'id',
              // SHAR-802: a CHB-xxxxxxxx reference is 12 monospace characters,
              // which did not fit in 120 with the cell's own padding, so every
              // id wrapped onto two lines.
              width: 160,
              fixed: 'left',
              render: (_, r) => <Text code>{r.reference ?? r.id.slice(-8)}</Text>,
            },
            {
              // The request this booking came from. Clicking it must not also
              // open the booking underneath, hence stopPropagation.
              title: 'Request',
              key: 'request',
              // SHAR-999 — 110 was narrower than the reference it holds
              // ("CH-" + eight characters in a monospace face), so the id broke
              // onto a second line at 1920px and below. The width is the one the
              // Reference column next to it already uses for the same shape of
              // value, and nowrap keeps it on one line whatever the zoom.
              width: 150,
              render: (_, r) => (
                <Text
                  code
                  style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    nav(`/charters/${r.requestId}`);
                  }}
                >
                  {r.requestReference}
                </Text>
              ),
            },
            { title: 'Flight', key: 'flight', width: 110, render: (_, r) => r.flightNumber || '—' },
            {
              title: 'User',
              key: 'user',
              width: 230,
              ellipsis: { showTitle: true },
              render: (_, r) => (r.user ? `${r.user.name ?? '—'} (${r.user.email})` : '—'),
            },
            { title: 'Tail', key: 'tail', width: 100, render: (_, r) => r.tailNumber || '—' },
            {
              title: 'Operator',
              key: 'op',
              width: 160,
              ellipsis: { showTitle: true },
              render: (_, r) => r.operatorName || '—',
            },
            { title: 'Model', key: 'model', width: 150, ellipsis: true, render: (_, r) => r.modelName || '—' },
            { title: 'Category', key: 'cat', width: 130, render: (_, r) => formatCategory(r.category) },
            { title: 'Seats', key: 'seats', width: 70, render: (_, r) => r.seatingCapacity ?? '—' },
            { title: 'Passengers', key: 'pax', width: 100, render: (_, r) => r.passengersCount },
            {
              title: 'Route',
              key: 'route',
              width: 260,
              // SHAR-734 — the cell WRAPS inside its own column.
              //
              // It was nowrap, so "EGCC - Manchester, Greater Manchester, United
              // Kingdom" simply ran on past the column's 260px and over the
              // departure date beside it. A route is the one column here whose
              // length is not ours to predict - it carries two city and country
              // names - so it is the one that has to be allowed to grow taller
              // rather than wider.
              render: (_, r) => (
                <div style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                  {airportLabel(r.originCode, r.originCity, r.originCountry)}
                  <br />→ {airportLabel(r.destCode, r.destCity, r.destCountry)}
                </div>
              ),
            },
            {
              title: 'Departure',
              key: 'dep',
              width: 170,
              // ...and the departure keeps its single line, so the date and time
              // never break in half next to a tall route cell.
              render: (_, r) => (
                <span style={{ whiteSpace: 'nowrap' }}>
                  {fmtZoned(r.departureZoned, dayjs(r.departureDateTime).format('DD/MM/YYYY HH:mm'))}
                </span>
              ),
            },
            {
              // Operator price stays in the currency it was agreed in, with the
              // GBP equivalent underneath, so neither figure is misread as the
              // other.
              title: 'Operator price',
              key: 'opprice',
              width: 150,
              render: (_, r) =>
                r.operatorPriceAmount != null ? (
                  <div style={{ whiteSpace: 'nowrap' }}>
                    {`${amount(r.operatorPriceAmount)} ${r.operatorPriceCcy ?? ''}`.trim()}
                    {r.operatorPriceGbp != null && r.operatorPriceCcy !== 'GBP' ? (
                      <>
                        <br />
                        <Text type="secondary">{gbp(r.operatorPriceGbp)}</Text>
                      </>
                    ) : null}
                  </div>
                ) : (
                  '—'
                ),
            },
            {
              title: 'Margin',
              key: 'margin',
              width: 110,
              render: (_, r) => (r.marginGbp != null ? `${gbp(r.marginGbp)}` : '—'),
            },
            {
              title: 'Total',
              key: 'total',
              width: 120,
              render: (_, r) => <Text strong>{gbp(r.totalGbp)}</Text>,
            },
            // SHAR-1028 - ad-hoc labels, as on the Bookings list. A wire booking
            // nobody has confirmed receipt of is money promised, not money in;
            // the admin can remove the label once it has been dealt with.
            {
              title: 'Ad-hoc status',
              key: 'adhoc',
              // SHAR-131 — fits "Wire transfer required" and its × on one line.
              width: 200,
              // Clicks inside the cell open the confirmation, not the booking.
              onCell: () => ({
                onClick: (e) => e.stopPropagation(),
                style: { cursor: 'default' },
              }),
              render: (_, r) =>
                r.adhocAwaitingTransfer || r.adhocPassportNeeded ? (
                  <Space size={4} wrap>
                    {r.adhocAwaitingTransfer ? (
                      <AdhocTag
                        color={ADHOC_COLORS.awaitingTransfer}
                        label="Wire transfer required"
                        title="Remove label"
                        description="Remove the “Wire transfer required” label from this booking? The payment stays not received until you mark it as received."
                        loading={dismissAdhoc.isPending}
                        onRemove={() => dismissAdhoc.mutate({ bookingId: r.id, label: 'transfer' })}
                      />
                    ) : null}
                    {/* SHAR-1027 - the same tag, colour and confirmation as on the
                        Bookings list. */}
                    {r.adhocPassportNeeded ? (
                      <AdhocTag
                        color={ADHOC_COLORS.passportNeededList}
                        label="Passport needed"
                        title="Remove label"
                        description="Remove the “Passport needed” label from this booking?"
                        loading={dismissAdhoc.isPending}
                        onRemove={() => dismissAdhoc.mutate({ bookingId: r.id, label: 'passport' })}
                      />
                    ) : null}
                  </Space>
                ) : (
                  <Text type="secondary">—</Text>
                ),
            },
            {
              title: 'Status',
              key: 'status',
              width: 190,
              fixed: 'right',
              render: (_, r) => (
                <Tag color={CHARTER_BOOKING_STATUS_COLORS[r.status]}>
                  {statusLabel(CHARTER_BOOKING_STATUS_LABELS, r.status, r.statusLabel)}
                </Tag>
              ),
            },
          ]}
        />
      </Card>
    </Space>
  );
}
